import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useMemberPerks } from "@/hooks/usePremium";
import { markAdsServed } from "@/lib/adsStatus";
import { trackAdClick, trackAdImpression, trackAdRequest, trackAdUnfilled } from "@/lib/adTracking";

const BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|preview/i;
const isBot = () => typeof navigator !== "undefined" && (BOT_UA.test(navigator.userAgent) || (navigator as { webdriver?: boolean }).webdriver === true);
import {
  AD_LABEL,
  AD_UNITS,
  AD_UNIT_MIN_HEIGHT,
  ADSENSE_PUBLISHER_ID,
  type AdUnitKey,
} from "@/lib/adUnits";

/** @deprecated import ADSENSE_PUBLISHER_ID from "@/lib/adUnits" instead. */
export const ADSENSE_CLIENT = ADSENSE_PUBLISHER_ID;

interface AdSlotProps {
  /** Which configured ad unit to render. */
  unit: AdUnitKey;
  format?: string;
  className?: string;
  label?: string;
  /** Reports AdSense's own fill verdict once known (used by `AdFill`). */
  onStatus?: (filled: boolean) => void;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let adsensePromise: Promise<void> | undefined;

function loadAdsense(): Promise<void> {
  if (window.adsbygoogle) return Promise.resolve();
  if (adsensePromise) return adsensePromise;
  adsensePromise = new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-statlign-adsense]");
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset["statlignAdsense"] = "1";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_PUBLISHER_ID}`;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => resolve(), { once: true });
    document.head.appendChild(script);
  });
  return adsensePromise;
}

/**
 * Performance-safe AdSense slot.
 *
 * The AdSense library itself is loaded once when a slot approaches the viewport,
 * so it never blocks LCP.
 * Each slot reserves its height up-front so filling an ad cannot shift layout,
 * and the slot ids come from `@/lib/adUnits` — the same module the prerenderer
 * uses, so the static and hydrated markup can never drift.
 */
/** Ad-free for Premium members and recent pack buyers. */
export function AdSlot(props: AdSlotProps) {
  const { perks } = useMemberPerks();
  if (perks) return null;
  return <AdSlotInner {...props} />;
}

function AdSlotInner({ unit, format = "auto", className, label = AD_LABEL, onStatus }: AdSlotProps) {
  const slot = AD_UNITS[unit];
  const minHeight = AD_UNIT_MIN_HEIGHT[unit];
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement | null>(null);
  const ctx = { unit, placement: String(unit) };

  useEffect(() => {
    if (pushed.current) return;
    const element = insRef.current;
    if (!element) return;
    let cancelled = false;
    const requestAd = async () => {
      if (pushed.current) return;
      pushed.current = true;
      if (!isBot()) trackAdRequest(ctx);
      await loadAdsense();
      if (cancelled) return;
      try {
        window.adsbygoogle?.push({});
      } catch { /* AdSense handles duplicate/blocked requests internally. */ }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void requestAd();
      },
      { rootMargin: "500px 0px" },
    );
    observer.observe(element);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  // Watch for AdSense's own fill marker. Only once a real ad has been served
  // may the site ask visitors to whitelist ads.
  useEffect(() => {
    const el = insRef.current;
    if (!el) return;
    let settled = false;
    let tracked = false;
    const track = (filled: boolean) => {
      if (tracked || !pushed.current || isBot()) return;
      tracked = true;
      (filled ? trackAdImpression : trackAdUnfilled)(ctx);
    };
    const settle = (filled: boolean) => {
      if (settled) return;
      settled = true;
      if (filled) markAdsServed();

      onStatus?.(filled);
    };
    const check = () => {
      const status = el.getAttribute("data-ad-status");
      if (status === "filled") {
        track(true);
        settle(true);
        return true;
      }
      if (status === "unfilled") {
        track(false);
        settle(false);
        return true;
      }
      return false;
    };
    if (check()) return;
    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect();
    });
    observer.observe(el, { attributes: true, attributeFilter: ["data-ad-status"] });
    // No verdict at all (script blocked, no inventory): treat as unfilled so a
    // second network can take the space.
    const timer = window.setTimeout(() => settle(false), 6000);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [onStatus]);

  // Clicks inside the ad iframe never reach the page; the window losing focus
  // while the pointer is over the ad is the standard signal. Once per slot.
  useEffect(() => {
    const el = insRef.current;
    if (!el || isBot()) return;
    let hovering = false;
    let clicked = false;
    const enter = () => { hovering = true; };
    const leave = () => { hovering = false; };
    const blur = () => {
      if (!hovering || clicked) return;
      clicked = true;
      trackAdClick(ctx);
    };
    el.addEventListener("pointerenter", enter);
    el.addEventListener("pointerleave", leave);
    window.addEventListener("blur", blur);
    return () => {
      el.removeEventListener("pointerenter", enter);
      el.removeEventListener("pointerleave", leave);
      window.removeEventListener("blur", blur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  return (
    // AdSense policy: ads must be clearly labelled and kept well away from
    // interactive controls (reveal toggles, vote buttons, tables) so a
    // mis-click is impossible. The generous vertical margin + divider is the
    // enforced separation; never render this inside an interactive card.
    <aside
      aria-label={label}
      data-statlign-ad-slot={slot}
      className={cn(
        "my-10 w-full overflow-hidden border-t border-border/60 pt-6",
        className,
      )}
    >
      <span className="mb-2 block text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <ins
        ref={insRef}
        className="adsbygoogle block"
        style={{ display: "block", minHeight }}
        data-ad-client={ADSENSE_PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
